import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, History, TrendingUp, Phone, 
  MessageSquare, Mail, ArrowRight, 
  Building2, Clock,
  FileText
} from 'lucide-react';
import { leadService } from '../../../services/erpApi';
import Avatar from '../../../components/Avatar/Avatar';
import './LeadContactsPage.css';

interface Communication {
  id: string;
  lead_id: string;
  lead_name: string;
  institution: string;
  type: 'phone' | 'message' | 'email';
  interaction_date: string;
  content: string;
  status: 'completed' | 'pending';
  created_by_name: string;
  created_by_avatar?: string | null;
}

const LeadContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    callsToday: 0,
    messagesToday: 0,
    emailsToday: 0
  });

  const calculateStats = useCallback((comms: Communication[]) => {
    const today = new Date().toISOString().split('T')[0];
    const callsToday = comms.filter(c => c.type === 'phone' && c.interaction_date?.startsWith(today)).length;
    const messagesToday = comms.filter(c => c.type === 'message' && c.interaction_date?.startsWith(today)).length;
    const emailsToday = comms.filter(c => c.type === 'email' && c.interaction_date?.startsWith(today)).length;
    
    setStats({ callsToday, messagesToday, emailsToday });
  }, []);

  const loadCommunications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await leadService.getAllCommunications({ next30Days: true });

      if (response.success) {
        setCommunications(response.data || []);
        calculateStats(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar comunicações:', error);
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  useEffect(() => {
    loadCommunications();
  }, [loadCommunications]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'phone': return <Phone size={16} />;
      case 'message': return <MessageSquare size={16} />;
      case 'email': return <Mail size={16} />;
      default: return <FileText size={16} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': 
        return <span className="communication-status-badge communication-status-completed">Concluído</span>;
      case 'pending': 
        return <span className="communication-status-badge communication-status-pending">Pendente</span>;
      default: 
        return <span className="communication-status-badge">N/A</span>;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredCommunications = communications.filter(comm => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      comm.lead_name?.toLowerCase().includes(search) ||
      comm.institution?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="lead-contacts-page">
        <div className="lead-contacts-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="lead-contacts-page">
      {/* Header */}
      <div className="lead-contacts-header">
        <div>
          <h1 className="lead-contacts-title">
            Fluxo de <span className="lead-contacts-title-accent">Comunicações</span>
          </h1>
          <p className="lead-contacts-subtitle">Auditoria e Análise de Contatos com Leads</p>
        </div>
        <div className="lead-contacts-header-actions">
          <button className="lead-contacts-btn-secondary">
            <History size={16} /> Histórico Completo
          </button>
          <button className="lead-contacts-btn-primary">
            <TrendingUp size={16} /> Relatório de Conversão
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="lead-contacts-stats">
        <div className="lead-contacts-stat-card">
          <div className="lead-contacts-stat-icon lead-contacts-stat-icon-blue">
            <Phone size={24} />
          </div>
          <div>
            <p className="lead-contacts-stat-label">Ligações Hoje</p>
            <p className="lead-contacts-stat-value">{stats.callsToday}</p>
          </div>
        </div>
        <div className="lead-contacts-stat-card">
          <div className="lead-contacts-stat-icon lead-contacts-stat-icon-green">
            <MessageSquare size={24} />
          </div>
          <div>
            <p className="lead-contacts-stat-label">WhatsApp Enviados</p>
            <p className="lead-contacts-stat-value">{stats.messagesToday}</p>
          </div>
        </div>
        <div className="lead-contacts-stat-card">
          <div className="lead-contacts-stat-icon lead-contacts-stat-icon-amber">
            <Mail size={24} />
          </div>
          <div>
            <p className="lead-contacts-stat-label">E-mails de Follow-up</p>
            <p className="lead-contacts-stat-value">{stats.emailsToday}</p>
          </div>
        </div>
      </div>

      {/* Visual Flow / Timeline */}
      <div className="lead-contacts-timeline-container">
        <div className="lead-contacts-timeline-header">
          <h3 className="lead-contacts-timeline-title">Fluxo Cronológico de Contatos</h3>
          <div className="lead-contacts-search-wrapper">
            <Search className="lead-contacts-search-icon" size={18} />
            <input 
              type="text" 
              placeholder="Filtrar por lead ou instituição..." 
              className="lead-contacts-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="lead-contacts-timeline">
          {filteredCommunications.length === 0 ? (
            <div className="lead-contacts-empty">
              <p>Nenhuma comunicação encontrada</p>
            </div>
          ) : (
            filteredCommunications.map((comm, idx) => (
              <div key={comm.id} className="lead-contacts-timeline-item">
                <div className="lead-contacts-timeline-dot">
                  {getTypeIcon(comm.type)}
                </div>

                <div className="lead-contacts-timeline-card">
                  <div className="lead-contacts-timeline-card-header">
                    <div className="lead-contacts-timeline-card-time">
                      <Clock size={12} />
                      <time>{formatDate(comm.interaction_date)}</time>
                    </div>
                    {getStatusBadge(comm.status)}
                  </div>
                  
                  <div className="lead-contacts-timeline-card-content">
                    <h4 className="lead-contacts-timeline-card-name">{comm.lead_name}</h4>
                    <p className="lead-contacts-timeline-card-institution">
                      <Building2 size={10} /> {comm.institution}
                    </p>
                  </div>

                  <p className="lead-contacts-timeline-card-summary">
                    "{comm.content || 'Sem descrição'}"
                  </p>

                  <div className="lead-contacts-timeline-card-footer">
                    <div className="lead-contacts-timeline-card-responsible">
                      <Avatar
                        name={comm.created_by_name}
                        email=""
                        imageUrl={comm.created_by_avatar ?? undefined}
                        size="sm"
                      />
                      <span>{comm.created_by_name}</span>
                    </div>
                    <button 
                      onClick={() => navigate(`/erp/prospecting/contacts/${comm.lead_id}`)}
                      className="lead-contacts-timeline-card-link"
                    >
                      Ver Lead <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadContactsPage;
